import { FlexBox } from '@airship-platform/jam-web/components';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Controller, useFormContext } from 'react-hook-form';
import { z } from 'zod';

export const DataCollectionSurveySchema = z.object({
  notes: z.string(),
  householdIncome: z
    .number()
    .nonnegative('Household income is required')
    .max(100000000, 'Household income cannot be more than 100000000'),
  householdSize: z
    .number()
    .min(1, 'Household size is required')
    .max(8, 'Household size cannot be more than 8'),
  householdOwner: z.enum(['rent', 'own']),
  taxFilingStatus: z.enum([
    'hoh',
    'joint',
    'married_filing_separately',
    'single',
    'qualifying_widower_with_dependent_child',
  ]),
  zipCode: z
    .string()
    .min(5, 'Enter a valid zip code')
    .regex(/^\d{5}$/, 'Enter a valid zip code'),
});

export type DataCollectionInputs = z.infer<typeof DataCollectionSurveySchema>;

const ownerDict: Record<DataCollectionInputs['householdOwner'], string> = {
  rent: 'Rent',
  own: 'Owner',
};

const taxDict: Record<DataCollectionInputs['taxFilingStatus'], string> = {
  hoh: 'Head of Household',
  joint: 'Joint',
  married_filing_separately: 'Married Filing Separately',
  single: 'Single',
  qualifying_widower_with_dependent_child:
    'Qualifying Widower with Dependent Child',
};

const DataCollectionFormDetails: React.FC = () => {
  const { control } = useFormContext<DataCollectionInputs>();

  return (
    <FlexBox flexDirection={'column'} gap={2}>
      <Typography variant="caption" sx={{ display: 'flex' }}>
        Enter a few personal details to surface your eligible tax incentives on
        systems and equipment. These details will not be shared with your home
        comfort advisor.
      </Typography>
      <Controller
        name="householdIncome"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            onChange={(e) => field.onChange(+e.target.value)}
            label="Household income"
            fullWidth
            margin="normal"
            type="number"
            error={!!error}
            helperText={error?.message}
          />
        )}
      />
      <Controller
        name="householdSize"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            onChange={(e) => field.onChange(+e.target.value)}
            label="Household size"
            fullWidth
            margin="normal"
            type="number"
            error={!!error}
            helperText={error?.message}
          />
        )}
      />
      <Controller
        name="taxFilingStatus"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            label="Tax filing status"
            fullWidth
            select
            margin="normal"
            error={!!error}
            helperText={error?.message}
          >
            {DataCollectionSurveySchema.shape.taxFilingStatus.options.map(
              (option) => (
                <MenuItem key={option} value={option}>
                  {taxDict[option]}
                </MenuItem>
              )
            )}
          </TextField>
        )}
      />
      <Controller
        name="zipCode"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            label="Zip Code"
            fullWidth
            margin="normal"
            error={!!error}
            helperText={error?.message}
          />
        )}
      />
      <Controller
        name="householdOwner"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            label="Household Owner"
            fullWidth
            select
            margin="normal"
            error={!!error}
            helperText={error?.message}
          >
            {DataCollectionSurveySchema.shape.householdOwner.options.map(
              (option) => (
                <MenuItem key={option} value={option}>
                  {ownerDict[option]}
                </MenuItem>
              )
            )}
          </TextField>
        )}
      />
    </FlexBox>
  );
};

export default DataCollectionFormDetails;
